/**
 * Updates the recently viewed products in localStorage.
 * Access is gated through Shopify Customer Privacy preferences consent.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Returns whether Shopify Customer Privacy currently permits preference processing.
   * Defaults closed if the API is unavailable or not ready.
   * @returns {boolean}
   */
  static #preferencesAllowed() {
    const customerPrivacy = window.Shopify?.customerPrivacy;
    return Boolean(
      customerPrivacy &&
      typeof customerPrivacy.preferencesProcessingAllowed === 'function' &&
      customerPrivacy.preferencesProcessingAllowed()
    );
  }

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    if (!this.#preferencesAllowed()) return;

    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from local storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    if (!this.#preferencesAllowed()) return [];
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}
